import React, { useEffect, Suspense, lazy, createElement } from 'react';
import { initPixel, setupGlobalTracking, track } from './lib/pixel';
const SocialProof = lazy(() => import('./components/SocialProof').then(m => ({
  default: m.SocialProof
})));
const PainPoints = lazy(() => import('./components/PainPoints').then(m => ({
  default: m.PainPoints
})));
const ProductShowcase = lazy(() => import('./components/ProductShowcase').then(m => ({
  default: m.ProductShowcase
})));
const WhatYouGet = lazy(() => import('./components/WhatYouGet').then(m => ({
  default: m.WhatYouGet
})));
const Bonuses = lazy(() => import('./components/Bonuses').then(m => ({
  default: m.Bonuses
})));
const Countdown = lazy(() => import('./components/Countdown').then(m => ({
  default: m.Countdown
})));
const Pricing = lazy(() => import('./components/Pricing').then(m => ({
  default: m.Pricing
})));
const ProBusiness = lazy(() => import('./components/ProBusiness').then(m => ({
  default: m.ProBusiness
})));
const Guarantee = lazy(() => import('./components/Guarantee').then(m => ({
  default: m.Guarantee
})));
const Testimonials = lazy(() => import('./components/Testimonials').then(m => ({
  default: m.Testimonials
})));
const FAQ = lazy(() => import('./components/FAQ').then(m => ({
  default: m.FAQ
})));
const DarkCTA = lazy(() => import('./components/DarkCTA').then(m => ({
  default: m.DarkCTA
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
const PurchaseNotifications = lazy(() => import('./components/PurchaseNotifications').then(m => ({
  default: m.PurchaseNotifications
})));
import { LazyMount } from './components/LazyMount';
const HERO_BASE = 'https://res.cloudinary.com/dowzlbqlz/image/upload';
const HERO_PATH = '/v1778382523/hf_20260509_035726_3b0b453a-cd00-4b5f-b77d-c15b35a1d726-Photoroom_1_g1m6xf.webp';
const HERO_IMG = `${HERO_BASE}/f_auto,q_auto,w_640,c_limit${HERO_PATH}`;
const HERO_SRCSET = `${HERO_BASE}/f_auto,q_auto,w_400,c_limit${HERO_PATH} 400w, ${HERO_BASE}/f_auto,q_auto,w_640,c_limit${HERO_PATH} 640w`;
const OG_IMG = 'https://cdn.magicpatterns.com/uploads/9uMhQHg3RXXbKPqANDF9n5/hf_20260423_164447_343907b5-95aa-49ff-ba71-eeea9d0be6f6-Photoroom.webp';
export function App() {
  useEffect(() => {
    const runSeo = () => {
      document.title = 'Megapack Nutrición Resuelta — Sistema Nutricional Completo™';
      document.documentElement.lang = 'es';
      const setMeta = (name: string, content: string, property?: boolean) => {
        const attr = property ? 'property' : 'name';
        let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
        if (!el) {
          el = document.createElement('meta');
          el.setAttribute(attr, name);
          document.head.appendChild(el);
        }
        el.content = content;
      };
      const description = 'Sistema clínico completo para nutricionistas que ya ejercen. 6 Tomos con metodología de primera consulta, diseño de plan, seguimiento y educación alimentaria + 6 bonos profesionales. Dejá de improvisar. Todo tu consultorio resuelto. Acceso inmediato en PDF.';
      const url = 'https://nutricionresuelta.com';
      const image = OG_IMG;
      setMeta('description', description);
      setMeta('keywords', 'megapack nutrición resuelta, sistema nutricional completo, nutricionistas argentina, primera consulta nutrición, seguimiento nutricional, educación alimentaria, planillas nutricionistas, protocolos nutrición argentina, construye progreso');
      setMeta('robots', 'index, follow');
      setMeta('author', 'Biblioteca Profesional™');
      setMeta('viewport', 'width=device-width, initial-scale=1');
      if (!document.querySelector('link[rel="canonical"]')) {
        const canonical = document.createElement('link');
        canonical.rel = 'canonical';
        canonical.href = url;
        document.head.appendChild(canonical);
      }
      setMeta('og:title', 'Megapack Nutrición Resuelta — El Sistema Nutricional Completo™', true);
      setMeta('og:description', description, true);
      setMeta('og:image', image, true);
      setMeta('og:url', url, true);
      setMeta('og:type', 'product', true);
      setMeta('og:locale', 'es_AR', true);
      setMeta('og:site_name', 'Biblioteca Profesional™', true);
      setMeta('twitter:card', 'summary_large_image');
      setMeta('twitter:title', 'Megapack Nutrición Resuelta — Sistema Nutricional Completo™');
      setMeta('twitter:description', description);
      setMeta('twitter:image', image);
      if (!document.querySelector('script[data-ld="product"]')) {
        const ld = document.createElement('script');
        ld.type = 'application/ld+json';
        ld.setAttribute('data-ld', 'product');
        ld.textContent = JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: 'Megapack Nutrición Resuelta — Sistema Nutricional Completo™',
          description,
          image,
          brand: {
            '@type': 'Brand',
            name: 'Biblioteca Profesional™'
          },
          offers: {
            '@type': 'AggregateOffer',
            lowPrice: '27950',
            highPrice: '34950',
            priceCurrency: 'ARS',
            availability: 'https://schema.org/InStock',
            offerCount: 2
          },
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '4.9',
            reviewCount: '634'
          }
        });
        document.head.appendChild(ld);
      }
      if (!document.querySelector('script[data-ld="org"]')) {
        const orgLd = document.createElement('script');
        orgLd.type = 'application/ld+json';
        orgLd.setAttribute('data-ld', 'org');
        orgLd.textContent = JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Biblioteca Profesional™',
          url,
          logo: image,
          email: 'soporte@bibliotecaprofesional.com',
          sameAs: ['https://www.instagram.com/biblioteca.profesional']
        });
        document.head.appendChild(orgLd);
      }
      if (!document.querySelector('script[data-ld="faq"]')) {
        const faqLd = document.createElement('script');
        faqLd.type = 'application/ld+json';
        faqLd.setAttribute('data-ld', 'faq');
        faqLd.textContent = JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [{
            '@type': 'Question',
            name: '¿Qué incluye el Megapack Nutrición Resuelta?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '6 Tomos clínicos con metodología completa: primera consulta estructurada en 5 etapas, diseño de plan adaptado a barreras reales, sistema de seguimiento con decisiones clínicas, educación alimentaria dosificada y sistema profesional de consultorio. Más 6 bonos con herramientas prácticas listas para usar.'
            }
          }, {
            '@type': 'Question',
            name: '¿Cuáles son las dos ofertas disponibles?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Oferta Base ($27.950): 6 Tomos clínicos completos + 6 Bonos profesionales. Oferta Completa ($34.950): todo lo anterior + planillas listas para consultorio, planes nutricionales adaptados a distintas necesidades, recetario completo y láminas imprimibles.'
            }
          }, {
            '@type': 'Question',
            name: '¿Para quién es este sistema?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Para nutricionistas que ya ejercen y quieren dejar de improvisar en consulta. Tanto para las que están arrancando como para las que tienen años de experiencia y quieren ordenar su práctica, mejorar la adherencia de sus pacientes y cobrar honorarios más altos con respaldo.'
            }
          }, {
            '@type': 'Question',
            name: '¿Cómo recibo el material?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Todo es 100% digital en formato PDF. Recibís acceso inmediato dentro de los 5 minutos posteriores a tu compra directamente en tu email.'
            }
          }, {
            '@type': 'Question',
            name: '¿Tiene garantía?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Sí. Ofrecemos garantía de 7 días sin preguntas. Si sentís que el sistema no resuelve tu consulta, te devolvemos el 100% de tu dinero.'
            }
          }, {
            '@type': 'Question',
            name: '¿Está adaptado a Argentina?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Sí, 100%. Los planes, recetas, protocolos y referencias clínicas están completamente adaptados al contexto argentino.'
            }
          }]
        });
        document.head.appendChild(faqLd);
      }
    };
    const ric = (window as any).requestIdleCallback;
    if (ric) {
      ric(runSeo, {
        timeout: 4000
      });
    } else {
      setTimeout(runSeo, 1500);
    }
  }, []);
  useEffect(() => {
    // Fire fbq('init') + fbq('track','PageView') immediately on load, exactly
    // like the literal Meta Pixel snippet. The stub is tiny, so this is cheap
    // and guarantees PageView attribution even on quick bounces. The heavy
    // fbevents.js script is still lazy-loaded on first interaction/idle.
    initPixel();
    // Global trackers (scroll depth, time-on-page, exit intent, etc.) can wait
    // for idle to keep the main thread free during initial paint.
    const ric = (window as any).requestIdleCallback;
    if (ric) {
      ric(() => setupGlobalTracking(), {
        timeout: 2000
      });
    } else {
      setTimeout(() => setupGlobalTracking(), 800);
    }
  }, []);
  return <>
      <noscript data-id="element-0">
        <img height="1" width="1" style={{
        display: 'none'
      }} src="https://www.facebook.com/tr?id=1215304437119773&ev=PageView&noscript=1" alt="" data-id="element-1" />
      </noscript>

      <header className="relative flex w-full flex-col items-center justify-center text-center text-gray-900 text-base leading-6 px-5 sm:px-6 py-12 sm:py-16" role="banner" style={{
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      backgroundImage: 'linear-gradient(135deg, rgb(240,255,244) 0%, rgb(220,252,231) 30%, rgb(240,255,244) 60%, rgb(248,250,252) 100%)',
      backgroundColor: 'rgb(255, 255, 255)'
    }} data-id="element-2">
        {/* ── 1. BADGE ── */}
        <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-green-600/30 bg-white/80 px-4 py-1.5 shadow-sm" data-id="element-3">
          <span className="text-xs sm:text-sm font-bold text-green-700 tracking-wide uppercase" data-id="element-4">
            🥑 Sistema Clínico Profesional · Nutricionistas · Argentina
          </span>
        </div>

        {/* ── 2. H1 ── */}
        <h1 className="sm:text-5xl md:text-[72px] sm:leading-tight md:leading-[80px] font-black max-w-4xl text-gray-900 m-0 mb-2 text-[34px] leading-[42px]" data-id="element-5">
          Dejá de improvisar
          <br data-id="element-6" />
          cada consulta.
          <br data-id="element-7" />
          <span className="text-green-700" data-id="element-8">Empezá a ejercer con sistema.</span>
        </h1>

        {/* ── 3. MOCKUP ── */}
        <div className="my-6 sm:my-8 w-full max-w-[320px] sm:max-w-[380px] aspect-square relative" data-id="element-9">
          <div className="absolute inset-4 rounded-full z-0 pointer-events-none" aria-hidden="true" style={{
          background: 'radial-gradient(circle, rgba(34,197,94,0.30) 0%, rgba(34,197,94,0.10) 50%, transparent 70%)'
        }} data-id="element-10" />
          <img src={HERO_IMG} srcSet={HERO_SRCSET} sizes="(max-width: 640px) 320px, 380px" alt="Megapack Nutrición Resuelta — Sistema Clínico Completo para Nutricionistas Argentina" width="400" height="400" fetchPriority="high" decoding="async" className="relative z-10 block max-w-full w-full h-auto object-contain" data-id="element-11" />
        </div>

        {/* ── 4. IDENTIDAD PROFESIONAL ── */}
        <p className="max-w-xl sm:text-lg font-semibold text-gray-700 m-0 mb-4 text-[15px]" data-id="element-12">
          El sistema que las nutricionistas más organizadas de Argentina usan
          para estructurar cada consulta con criterio clínico, hacer
          seguimientos que producen decisiones reales y{' '}
          <span className="text-gray-900 font-black" data-id="element-13">
            cobrar lo que realmente vale su trabajo.
          </span>
        </p>

        {/* ── 5. TAGLINE MECANISMO ── */}
        <p className="max-w-xl sm:text-xl font-black text-green-700 m-0 mb-6 text-center text-[17px]" data-id="element-14">
          "6 tomos clínicos + 6 bonos profesionales. Primera consulta, diseño de
          plan, seguimiento y educación alimentaria. Todo tu consultorio
          resuelto en un solo lugar."
        </p>

        {/* ── 6. TAGS ── */}
        <div className="mb-7 flex max-w-lg flex-wrap items-center justify-center gap-2" data-id="element-15">
          {['📄 100% PDF', '⚡ Acceso Inmediato', '🇦🇷 Argentina', '🥑 Nutrición Clínica', '📚 6 Tomos + 6 Bonos', '✅ Sistema Listo para Usar', '🗂️ Planillas + Recursos'].map(tag => <span key={tag} className="whitespace-nowrap bg-white/80 text-xs sm:text-sm font-semibold text-gray-900 leading-5 rounded-full px-3 py-1 shadow-sm" style={{
          border: '1px solid rgba(21,128,61,0.30)'
        }} data-id="element-16">
              {tag}
            </span>)}
        </div>

        {/* ── 7. CTA ── */}
        <a href="#pricing" onClick={() => track('Lead', {
        content_name: 'Hero CTA Click',
        cta_location: 'hero'
      })} className="block w-full max-w-sm bg-green-700 text-white text-lg sm:text-xl font-black rounded-xl px-6 sm:px-8 py-4 mb-5 no-underline text-center shadow-lg hover:opacity-90 transition-opacity" data-id="element-17">
          🥑 → QUIERO MI MEGAPACK AHORA
        </a>

        {/* ── 8. TRUST BADGES ── */}
        <div className="flex flex-row flex-wrap items-center justify-center gap-3 sm:gap-4 m-0" data-id="element-18">
          <span className="inline-flex items-center gap-1.5 bg-white/80 border border-gray-200 rounded-full px-3 py-1.5 text-xs sm:text-sm text-gray-600 shadow-sm" data-id="element-19">
            🛡️ Garantía 7 días
          </span>
          <span className="inline-flex items-center gap-1.5 bg-white/80 border border-gray-200 rounded-full px-3 py-1.5 text-xs sm:text-sm text-gray-600 shadow-sm" data-id="element-20">
            🔒 Pago seguro
          </span>
          <span className="inline-flex items-center gap-1.5 bg-white/80 border border-gray-200 rounded-full px-3 py-1.5 text-xs sm:text-sm text-gray-600 shadow-sm" data-id="element-21">
            💳 Mercado Pago | Tarjeta | Transferencia
          </span>
        </div>
      </header>

      <main data-id="element-22">
        {/* First section — load eagerly, it's right under the hero */}
        <Suspense fallback={null} data-id="element-23">
          <SocialProof data-id="element-24" />
        </Suspense>

        {/* Everything below the fold mounts only as it nears the viewport,
            so each chunk's download + parse + execute is deferred. */}
        <Suspense fallback={null} data-id="element-25">
          <LazyMount minHeight={500} data-id="element-26">
            <PainPoints data-id="element-27" />
          </LazyMount>
          <LazyMount minHeight={700} data-id="element-28">
            <ProductShowcase data-id="element-29" />
          </LazyMount>
          <LazyMount minHeight={600} data-id="element-30">
            <WhatYouGet data-id="element-31" />
          </LazyMount>
          <LazyMount minHeight={800} data-id="element-32">
            <Bonuses data-id="element-33" />
          </LazyMount>
          <LazyMount minHeight={400} data-id="element-34">
            <Countdown data-id="element-35" />
          </LazyMount>
          <LazyMount minHeight={600} id="pricing" data-id="element-36">
            <Pricing data-id="element-37" />
          </LazyMount>
          <LazyMount minHeight={700} data-id="element-38">
            <ProBusiness data-id="element-39" />
          </LazyMount>
          <LazyMount minHeight={300} data-id="element-40">
            <Guarantee data-id="element-41" />
          </LazyMount>
          <LazyMount minHeight={500} data-id="element-42">
            <Testimonials data-id="element-43" />
          </LazyMount>
          <LazyMount minHeight={500} data-id="element-44">
            <FAQ data-id="element-45" />
          </LazyMount>
          <LazyMount minHeight={700} data-id="element-46">
            <DarkCTA data-id="element-47" />
          </LazyMount>
        </Suspense>
      </main>

      <Suspense fallback={null} data-id="element-48">
        <LazyMount minHeight={200} data-id="element-49">
          <Footer data-id="element-50" />
        </LazyMount>
        <PurchaseNotifications data-id="element-51" />
      </Suspense>
    </>;
}